import { Tooltip } from 'antd';
import { DeleteOutlined, ClearOutlined, MinusCircleOutlined } from '@ant-design/icons';
import {
  conditionCategories,
  conditionSymbols,
  toothColors,
  resolveConditionColor,
  eraserCodes,
} from './odontogramConfig';

const ColorSelectorRow = ({ activeColor, onSelectColor, disabled }) => (
  <div className="odo-menu-color-row">
    <button
      className={`odo-color-btn odo-color-btn--blue${activeColor === toothColors.healthy ? ' active' : ''}`}
      onClick={() => onSelectColor(toothColors.healthy)}
      disabled={disabled}
      type="button"
    >
      Azul
    </button>
    <button
      className={`odo-color-btn odo-color-btn--red${activeColor === toothColors.pathological ? ' active' : ''}`}
      onClick={() => onSelectColor(toothColors.pathological)}
      disabled={disabled}
      type="button"
    >
      Rojo
    </button>
  </div>
);

const EraserRow = ({ activeFinding, onSelectFinding, disabled }) => {
  const eraserSingle = { code: eraserCodes.single, label: 'Borrador individual', eraser: 'single' };
  const eraserMulti = { code: eraserCodes.multi, label: 'Borrador múltiple', eraser: 'multi' };
  const cpodExclude = { code: eraserCodes.cpodExclude, label: 'Excluir del CPOD', eraser: 'cpodExclude' };

  return (
    <div className="odo-menu-eraser-row">
      <Tooltip title="Borra la última condición de una pieza">
        <button
          className={`odo-eraser-btn${activeFinding?.code === eraserSingle.code ? ' active' : ''}`}
          onClick={() => onSelectFinding(eraserSingle)}
          disabled={disabled}
          type="button"
        >
          <ClearOutlined />
          <span>Borrador<br />Individual</span>
        </button>
      </Tooltip>
      <Tooltip title="Borra todas las condiciones de una pieza">
        <button
          className={`odo-eraser-btn${activeFinding?.code === eraserMulti.code ? ' active' : ''}`}
          onClick={() => onSelectFinding(eraserMulti)}
          disabled={disabled}
          type="button"
        >
          <DeleteOutlined />
          <span>Borrador<br />Múltiple</span>
        </button>
      </Tooltip>
      <Tooltip title="Excluir una pieza permanente del índice CPOD">
        <button
          className={`odo-eraser-btn${activeFinding?.code === cpodExclude.code ? ' active' : ''}`}
          onClick={() => onSelectFinding(cpodExclude)}
          disabled={disabled}
          type="button"
        >
          <MinusCircleOutlined />
          <span>Excluir<br />CPOD</span>
        </button>
      </Tooltip>
    </div>
  );
};

const ConditionRow = ({ condition, isActive, activeSubCode, activeColor, onSelect, disabled }) => {
  const effectiveColor = resolveConditionColor(condition, activeColor);
  const symbolFn = conditionSymbols[condition.symbolKey ?? condition.code];

  return (
    <>
      <button
        className={`odo-condition-item${isActive ? ' active' : ''}`}
        onClick={() => !disabled && onSelect(condition)}
        disabled={disabled}
        type="button"
      >
        <span className="odo-condition-symbol">
          {symbolFn ? symbolFn(effectiveColor, 18) : (
            <svg width="27" height="18" viewBox="0 0 27 18" fill="none">
              <text x="0" y="14" fontSize="10" fontWeight="bold" fill={effectiveColor} fontFamily="monospace">
                {condition.gridCode || condition.code.slice(0, 3)}
              </text>
            </svg>
          )}
        </span>
        <Tooltip title={condition.label} placement="right" overlayClassName="odo-surface-tooltip" mouseEnterDelay={0.5}>
          <span className="odo-condition-label">{condition.label}</span>
        </Tooltip>
        {condition.colorMode !== 'user' && (
          <span className="odo-condition-color-dot" style={{ background: effectiveColor }} />
        )}
      </button>

      {isActive && condition.showOptions !== false && condition.options?.length > 0 && (
        <div className="odo-sub-options">
          {condition.options.map(option => (
            <button
              key={option.code}
              type="button"
              className={`odo-sub-pill${activeSubCode === option.code ? ' active' : ''}`}
              style={{
                color: effectiveColor, borderColor: effectiveColor,
                background: activeSubCode === option.code ? effectiveColor : 'transparent'
              }}
              onClick={() => !disabled && onSelect({ ...condition, subCode: option.code })}
              disabled={disabled}
            >
              {option.icon
                ? option.icon(activeSubCode === option.code ? '#fff' : effectiveColor)
                : <span style={{ color: activeSubCode === option.code ? '#fff' : effectiveColor }}>
                  {option.label.length <= 2 ? option.label : `${option.code} – ${option.label}`}
                </span>
              }
            </button>
          ))}
        </div>
      )}
    </>
  );
};

const CategorySection = ({ category, activeFinding, activeColor, onSelectFinding, disabled }) => (
  <div className="odo-category-section">
    <div className="odo-category-label">{category.label}</div>
    {category.conditions.map(condition => (
      <ConditionRow
        key={condition.code}
        condition={condition}
        isActive={activeFinding?.code === condition.code}
        activeSubCode={activeFinding?.code === condition.code ? activeFinding?.subCode : null}
        activeColor={activeColor}
        onSelect={onSelectFinding}
        disabled={disabled}
      />
    ))}
  </div>
);

const OdontogramMenu = ({
  activeFinding,
  onSelectFinding,
  activeColor,
  onSelectColor,
  disabled = false,
}) => (
  <div className="odo-menu-card">
    <div className="odo-menu-title">Condiciones</div>
    <ColorSelectorRow
      activeColor={activeColor}
      onSelectColor={onSelectColor}
      disabled={disabled}
    />

    <EraserRow
      activeFinding={activeFinding}
      onSelectFinding={onSelectFinding}
      disabled={disabled}
    />

    <div className="odo-menu-divider" />

    <div className="odo-menu-scroll">
      {conditionCategories
        .filter(category => category.key !== 'clinicalIndices')
        .map(category => (
          <CategorySection
            key={category.key}
            category={category}
            activeFinding={activeFinding}
            activeColor={activeColor}
            onSelectFinding={onSelectFinding}
            disabled={disabled}
          />
        ))}
    </div>
  </div>
);

export default OdontogramMenu;
